import { useQuery } from '@tanstack/react-query';
import { Button, Col, Divider, Form, FormInstance, Row } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { userApi } from '../../../../apis';
import { CreateUserDto, UserRoleEnum } from '../../../../apis/client-axios';
import DatePickerWrap from '../../../../components/DatePickerWrap';
import InputWrap from '../../../../components/InputWrap';
import SelectWrap from '../../../../components/SelectWrap';
import { QUERY_KEY } from '../../../../constants/QueryKey';
import './style.scss';

interface FormStaffProps {
  form: FormInstance;
}

interface StaffOptions {
  genders: DefaultOptionType[];
  roles: DefaultOptionType[];
  recruitmentTypes: DefaultOptionType[];
  departments: DefaultOptionType[];
  positions: DefaultOptionType[];
  managers: DefaultOptionType[];
}

const n = (key: keyof CreateUserDto) => key;

const FormStaff: FC<FormStaffProps> = (props) => {
  const { form } = props;

  const intl = useIntl();

  const employeeApprovals = Form.useWatch('employeeApprovals', form);
  const role = Form.useWatch('role', form);

  const [options, setOptions] = useState<StaffOptions>({
    genders: [],
    roles: [],
    recruitmentTypes: [],
    departments: [],
    positions: [],
    managers: [],
  });

  const { data: employeeRelatedInformationResponse } = useQuery({
    queryKey: [QUERY_KEY.USER_RELATED_INFORMATION],
    queryFn: () => userApi.userControllerGetEmployeeRelatedInformation(),
  });

  useEffect(() => {
    if (employeeRelatedInformationResponse?.data?.data) {
      const { genders, roles, recruitmentTypes, departments, positions, managers } =
        employeeRelatedInformationResponse?.data?.data;

      setOptions({
        genders: genders.map((gender) => ({
          value: gender,
          label: intl.formatMessage({ id: `account-form.gender.${gender}` }),
        })),
        roles: roles.map((role) => ({
          value: role,
          label: intl.formatMessage({ id: `account-form.role.${role}` }),
        })),
        recruitmentTypes: recruitmentTypes.map((recruitmentType) => ({
          value: recruitmentType.id,
          label: recruitmentType.type,
        })),
        departments: departments.map((department) => ({
          value: department.id,
          label: department.name,
        })),
        positions: positions.map((position) => ({
          value: position.id,
          label: position.name,
        })),
        managers: managers.map((manager) => ({
          value: manager.id,
          label: manager.name,
        })),
      });
    }
  }, [employeeRelatedInformationResponse?.data?.data, intl]);

  const validatePhoneNumber = (_, value) => {
    if (!value) {
      return Promise.resolve();
    }
    if (!/^(?:\d{11}|\d{3}-\d{3}-\d{4}|\d{2}-\d{4}-\d{4}|\d{3}-\d{4}-\d{4})$/.test(value)) {
      return Promise.reject(intl.formatMessage({ id: 'account-form.phoneNumber.validate' }));
    }
    return Promise.resolve();
  };

  const validateFuriganaName = (_, value) => {
    if (!value) {
      return Promise.resolve();
    }
    if (!/^[\u3040-\u30FF\u4E00-\u9FFFー\s]+$/.test(value)) {
      return Promise.reject(intl.formatMessage({ id: 'account-form.furiganaName.validate' }));
    }
    return Promise.resolve();
  };

  const validatePositiveIntegerWithRange = (min?, max?) => (_, value) => {
    if (!value) {
      return Promise.resolve();
    }
    if (!/^\d*\.?\d{1}$/.test(value)) {
      return Promise.reject(intl.formatMessage({ id: 'common.invalidFormat' }));
    }
    // if (value < min || value > max) {
    //   return Promise.reject();
    // }
    return Promise.resolve();
  };

  // const validatePassword = (_, value) => {
  //   if (!value) {
  //     return Promise.resolve();
  //   }
  //   if (!/^(?=.*[0-9])(?=.*[a-zA-Z])(?=.*[!@#$%^&*(){};'<> ..])(?=.*\S).{6,}$/.test(value)) {
  //     return Promise.reject(intl.formatMessage({ id: 'account-form.password.validate' }));
  //   }
  //   return Promise.resolve();
  // };

  return (
    <div className="form-staff">
      <Row gutter={[16, 0]}>
        <Col span={12}>
          <Form.Item
            name={n('name')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.name' })}
            rules={[
              { required: true, message: intl.formatMessage({ id: 'common.required' }) },
              {
                whitespace: true,
                message: intl.formatMessage(
                  { id: 'common.validate.required' },
                  { field: intl.formatMessage({ id: 'account-form.name' }) },
                ),
              },
            ]}
          >
            <InputWrap
              maxLength={255}
              className="width-full"
              placeholder={intl.formatMessage({ id: 'account-form.name.placeholder' })}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('furiganaName')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.furiganaName' })}
            rules={[
              { required: true, message: intl.formatMessage({ id: 'common.required' }) },
              { whitespace: true, message: intl.formatMessage({ id: 'common.required' }) },
              {
                validator: validateFuriganaName,
              },
            ]}
          >
            <InputWrap
              maxLength={255}
              className="width-full"
              placeholder={intl.formatMessage({ id: 'account-form.furiganaName.placeholder' })}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('gender')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.gender' })}
            rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
          >
            <SelectWrap
              className="width-full"
              options={options.genders}
              placeholder={intl.formatMessage({ id: 'account-form.gender.placeholder' })}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('dateOfBirth')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.dateOfBirth' })}
            rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
          >
            <DatePickerWrap className="width-full" maxDate={dayjs()} placeholder="yyyy/mm/dd" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('email')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.email' })}
            rules={[{ type: 'email', message: intl.formatMessage({ id: 'account-form.email.validate' }) }]}
          >
            <InputWrap
              maxLength={255}
              className="width-full"
              placeholder={intl.formatMessage({ id: 'account-form.email.placeholder' })}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('phoneNumber')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.phoneNumber' })}
            rules={[
              { required: true, message: intl.formatMessage({ id: 'common.required' }) },
              { whitespace: true, message: intl.formatMessage({ id: 'common.required' }) },
              {
                validator: validatePhoneNumber,
              },
            ]}
          >
            <InputWrap
              maxLength={255}
              className="width-full"
              placeholder={intl.formatMessage({ id: 'account-form.phoneNumber.placeholder' })}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="address" className="custom-label" label={intl.formatMessage({ id: 'account-form.address' })}>
            <InputWrap
              maxLength={255}
              className="width-full"
              placeholder={intl.formatMessage({ id: 'account-form.address.placeholder' })}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Divider className="form-divider" />
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('employeeCode')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.employeeCode' })}
            rules={[
              { required: true, message: intl.formatMessage({ id: 'common.required' }) },
              {
                whitespace: true,
                message: intl.formatMessage(
                  { id: 'common.validate.required' },
                  { field: intl.formatMessage({ id: 'account-form.employeeCode' }) },
                ),
              },
            ]}
          >
            <InputWrap
              maxLength={255}
              className="width-full"
              placeholder={intl.formatMessage({ id: 'account-form.employeeCode.placeholder' })}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('displayPassword')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.password' })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'common.required' }),
              },
              {
                whitespace: true,
                message: intl.formatMessage({ id: 'common.required' }),
              },
              // {
              //   validator: validatePassword,
              // },
            ]}
          >
            <InputWrap
              maxLength={255}
              className="width-full"
              placeholder={intl.formatMessage({ id: 'account-form.password.placeholder' })}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Divider className="form-divider" />
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('officialWorkingDay')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.officialWorkingDay' })}
            rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
          >
            <DatePickerWrap className="width-full" maxDate={dayjs()} placeholder="yyyy/mm/dd" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('recruitmentTypeId')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.recruitmentType' })}
            rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
          >
            <SelectWrap
              className="width-full"
              placeholder={intl.formatMessage({ id: 'account-form.recruitmentType.placeholder' })}
              options={options.recruitmentTypes}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Divider className="form-divider" />
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('departmentId')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.department' })}
            rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
          >
            <SelectWrap
              className="width-full"
              placeholder={intl.formatMessage({ id: 'account-form.department.placeholder' })}
              options={options.departments}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('positionId')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.position' })}
            rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
          >
            <SelectWrap
              className="width-full"
              placeholder={intl.formatMessage({ id: 'account-form.position.placeholder' })}
              options={options.positions}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Divider className="form-divider" />
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('role')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.role' })}
            rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
          >
            <SelectWrap
              className="width-full"
              placeholder={intl.formatMessage({ id: 'account-form.role.placeholder' })}
              options={options.roles}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.List name={n('employeeApprovals')} initialValue={[{}]}>
            {(fields, { add, remove }) => (
              <div className="approver-fields">
                <div className="approver-header">
                  <div className="approver-label">
                    <span>{intl.formatMessage({ id: 'staff-management.approver' })}</span>
                    {role === UserRoleEnum.Employee && <span className="text-red">* </span>}
                  </div>
                  {fields?.length < 2 ? (
                    <div onClick={() => add({})} className="btn-add">
                      + {intl.formatMessage({ id: 'staff-management.addApprover' })}
                    </div>
                  ) : (
                    <div className="btn-add-unactive">
                      + {intl.formatMessage({ id: 'staff-management.addApprover' })}
                    </div>
                  )}
                </div>
                <div className="fields-container">
                  {fields?.map((field, index) => {
                    const approvals = employeeApprovals ? [...employeeApprovals] : [];
                    approvals.splice(index, 1);

                    const managerOptions = options.managers.filter(
                      (option) => approvals.findIndex((approver) => approver?.managerId === option.value) === -1,
                    );

                    return (
                      <div>
                        <div className="approver-header header-chill">
                          <div className="approver-label">
                            <span>{`承認者 ${field.name + 1}`}</span>
                          </div>
                          <Button
                            variant="text"
                            color="danger"
                            disabled={fields?.length <= 1}
                            onClick={() => {
                              remove(field.name);
                            }}
                            className="remove-item"
                          >
                            {intl.formatMessage({ id: 'common.remove' })}
                          </Button>
                        </div>
                        <Form.Item
                          name={[field.name, 'managerId']}
                          className={role === UserRoleEnum.Employee ? '' : 'hidden-validate'}
                          rules={[
                            {
                              required: role === UserRoleEnum.Employee,
                              message: intl.formatMessage({ id: 'common.required' }),
                            },
                          ]}
                        >
                          <SelectWrap
                            className="width-full"
                            placeholder={intl.formatMessage({ id: 'account-form.managerId.placeholder' })}
                            options={managerOptions}
                            allowClear={true}
                          />
                        </Form.Item>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Form.List>
        </Col>

        <Col span={24}>
          <Divider className="form-divider" />
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('annualLeaveDaysRemaining')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.annualLeaveDaysRemaining' })}
            rules={[
              {
                validator: validatePositiveIntegerWithRange(1, 22),
              },
            ]}
          >
            <InputWrap className="width-full" max={22} min={0} maxLength={11} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={n('compensatoryLeaveDaysRemaining')}
            className="custom-label"
            label={intl.formatMessage({ id: 'account-form.compensatoryLeaveDaysRemaining' })}
            rules={[
              {
                validator: validatePositiveIntegerWithRange(1, 22),
              },
            ]}
          >
            <InputWrap className="width-full" max={22} min={0} maxLength={11} />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

export default FormStaff;
